import { Alert, Link } from "@amzn/awsui-components-react";
import { JSX } from "react";
import {
    PROD_DOMAIN,
    GAMMA_DOMAIN,
    BETA_DOMAIN,
    DEV_DOMAIN,
    LOCAL_DOMAIN,
} from "configuration/config";

/**
 * Configuration for the URL migration announcement
 */
const URL_MIGRATION_CONFIG = {
    deprecationDate: "July 30, 2026",
    enabled: true,
};

/**
 * Returns the new URL based on the current hostname/stage.
 * Maps the current environment to the corresponding new domain.
 *
 * @returns The new URL for the current stage
 */
const getNewUrlForCurrentStage = (): string => {
    const hostname = location.hostname;

    if (hostname.includes("gamma")) {
        return `https://${GAMMA_DOMAIN}`;
    }
    if (hostname.includes("beta")) {
        return `https://${BETA_DOMAIN}`;
    }
    if (hostname.includes("dev")) {
        return `https://${DEV_DOMAIN}`;
    }
    if (hostname.startsWith("local")) {
        return `https://${LOCAL_DOMAIN}`;
    }
    return `https://${PROD_DOMAIN}`;
};

/**
 * Banner component to notify users about the URL migration.
 * This banner is displayed on every page to inform users that the
 * website URL is changing and they should update their bookmarks.
 *
 * @returns {JSX.Element | null} The migration banner or null if disabled
 */
const UrlMigrationBanner = (): JSX.Element | null => {
    if (!URL_MIGRATION_CONFIG.enabled) {
        return null;
    }

    const newUrl = getNewUrlForCurrentStage();

    return (
        <Alert
            type="warning"
            header="Important: Website URL is changing"
        >
            The ITSE Website URL is moving to a new address. Please update your bookmarks to{" "}
            <Link href={newUrl} external>
                {newUrl}
            </Link>
            . The current URL will no longer be available starting{" "}
            <strong>{URL_MIGRATION_CONFIG.deprecationDate}</strong>.
        </Alert>
    );
};

export default UrlMigrationBanner;
